import { useState, useCallback, useEffect } from "react";
import { atom, useRecoilState } from "recoil";
import { IAccount, IdentificationStatus } from "src/domains";
import { AppState, AppStateStatus } from "react-native";
import { EventRegister } from "react-native-event-listeners";
import { logoutEmit } from "src/utils/event";
import {
  TabChangeEvent,
  TransactionBuyEvent,
  TransactionCreditCardEvent,
  LogoutEvent,
  AccountUpdateIdentificationEvent,
  accountUpdateIdentificationEmit,
  WithdrawalEvent,
} from "src/utils/event";
import {
  queryGetAccount,
  mutationAccountUpdate,
  IMutationAccountUpdateRequest,
  mutationAccountPhoneConfirm,
  IMutationAccountPhoneConfirmRequest,
  mutationAccountUpdateIdentification,
  mutationAccountUpdatePhone,
  mutationAccountIsNicknameExist,
  mutationAccountIsNicknameExistForUnauth,
  IMutationAccountIsNicknameExistRequest,
  mutationAccountRemove,
} from "src/graphql";
import { useCognito } from "./cognito";
import { uploadToS3 } from "src/utils/s3";
import { v4 as uuid } from "uuid";
import { useSignOut } from "src/hooks";
import { navigationRef } from "./navigation";

export const accountState = atom<IAccount | undefined>({
  key: "accountState",
  default: undefined,
});

export const useAccount = () => {
  const [account, setAccount] = useRecoilState<IAccount | undefined>(
    accountState
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetAccount();
      if (_isMount) {
        setAccount(response.data?.getAccount);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setAccount]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "MyPage") {
          refresh();
        }
      }
    );
    const transactionBuyListener = EventRegister.addEventListener(
      TransactionBuyEvent,
      () => refresh()
    );

    const transactionCreditCardListener = EventRegister.addEventListener(
      TransactionCreditCardEvent,
      () => refresh()
    );

    const accountUpdateIdentificationEventListener =
      EventRegister.addEventListener(AccountUpdateIdentificationEvent, () =>
        refresh()
      );

    const withdrawalListener = EventRegister.addEventListener(
      WithdrawalEvent,
      () => refresh()
    );

    return () => {
      EventRegister.removeEventListener(tabChangeListener as string);
      EventRegister.removeEventListener(transactionBuyListener as string);
      EventRegister.removeEventListener(
        transactionCreditCardListener as string
      );
      EventRegister.removeEventListener(
        accountUpdateIdentificationEventListener as string
      );
      EventRegister.removeEventListener(withdrawalListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setAccount(undefined);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [account, loading, error, refresh] as const;
};

export const useAccountUpdateAction = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(async (params: IMutationAccountUpdateRequest) => {
    setError(undefined);
    setLoading(true);
    try {
      await mutationAccountUpdate(params);
      setLoading(false);
      return true;
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return false;
    }
  }, []);

  return [action, loading, error] as const;
};

export const useAccountUploadImageAction = () => {
  const [cognitoUsername] = useCognito();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(async (url: string) => {
    setError(undefined);
    setLoading(true);
    try {
      if (!cognitoUsername) {
        throw Error("Cognito user is empty");
      }
      const response = await uploadToS3(
        url,
        `user-${cognitoUsername}/avatar-${uuid() as string}`
      );
      setLoading(false);
      return response;
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return undefined;
    }
  }, []);

  return [action, loading, error] as const;
};

export const useAccountUploadVoiceAction = () => {
  const [cognitoUsername] = useCognito();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(async (url: string) => {
    setError(undefined);
    setLoading(true);
    try {
      if (!cognitoUsername) {
        throw Error("Cognito user is empty");
      }
      const extension = /[.]/.exec(url) ? /[^.]+$/.exec(url) : undefined;
      const response = await uploadToS3(
        url,
        `user-${cognitoUsername}/voice-${uuid() as string}`,
        "public",
        Array.isArray(extension) && extension.length > 0 ? extension[0] : null
      );
      setLoading(false);
      return response;
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return undefined;
    }
  }, []);

  return [action, loading, error] as const;
};

export const useAccountUploadDocumentAction = () => {
  const [cognitoUsername] = useCognito();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(async (url: string) => {
    setError(undefined);
    setLoading(true);
    try {
      if (!cognitoUsername) {
        throw Error("Cognito user is empty");
      }
      const response = await uploadToS3(
        url,
        `user-${cognitoUsername}/identification-${uuid() as string}`
      );
      setLoading(false);
      return response;
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return undefined;
    }
  }, []);

  return [action, loading, error] as const;
};

export const useAccountPhoneConfirmCode = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(
    async (params: IMutationAccountPhoneConfirmRequest) => {
      setError(undefined);
      setLoading(true);
      try {
        await mutationAccountPhoneConfirm(params);
        setLoading(false);
        return true;
      } catch (e) {
        console.error(e);
        setError(e as Error);
        setLoading(false);
        return false;
      }
    },
    []
  );

  return [action, loading, error] as const;
};

export const useAccountUpdateIdentification = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(
    async (params: {
      phone: string;
      code: string;
      documents: Array<string>;
    }) => {
      setError(undefined);
      setLoading(true);
      try {
        await mutationAccountUpdatePhone({
          phone: params.phone,
          code: params.code,
        });
        await mutationAccountUpdateIdentification({
          input: {
            identificationStatus: IdentificationStatus.PROCESSED,
            identificationDocuments: JSON.stringify(params.documents),
            identificationMessage: undefined,
          },
        });
        setLoading(false);
        accountUpdateIdentificationEmit();
        return true;
      } catch (e: any) {
        console.error(e);
        if (e.errors && Array.isArray(e.errors)) {
          setError(new Error(e.errors[0].message));
        } else {
          setError(e as Error);
        }
        setLoading(false);
        return false;
      }
    },
    []
  );

  return [action, loading, error] as const;
};

export const useAccountIsNicknameExistAction = (unauth: boolean = false) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const action = useCallback(
    async (params: IMutationAccountIsNicknameExistRequest) => {
      setError(undefined);
      setLoading(true);
      try {
        const response = unauth
          ? await mutationAccountIsNicknameExistForUnauth(params)
          : await mutationAccountIsNicknameExist(params);
        setLoading(false);
        return response.data.accountIsNicknameExist;
      } catch (e) {
        console.error(e);
        setError(e as Error);
        setLoading(false);
        return undefined;
      }
    },
    []
  );

  return [action, loading, error] as const;
};

export const useAccountRemove = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const authSignOut = useSignOut();
  const removeHandler = async () => {
    setError(undefined);
    setLoading(true);
    try {
      await mutationAccountRemove();
      await authSignOut();
      setLoading(false);
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
    }
  };

  return [removeHandler, loading, error] as const;
};
